// src/components/dashboard/PostCard.jsx - UPDATED: Interactive platform selection
import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { postsAPI } from '../../services/api';
import { motion } from 'framer-motion';
import {
    CheckIcon,
    XMarkIcon,
    ArrowPathIcon,
    RocketLaunchIcon,
    TrashIcon,
    Cog6ToothIcon,
    CheckCircleIcon, DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function PostCard({ post }) {
    const [showPlatformSelector, setShowPlatformSelector] = useState(false);
    const [selectedPlatforms, setSelectedPlatforms] = useState(post.platforms || []);
    const [fullScreenImage, setFullScreenImage] = useState(null);
    const [isEditingCaption, setIsEditingCaption] = useState(false);
    const [editedCaption, setEditedCaption] = useState(post.caption || '');
    const [isEditingHashtags, setIsEditingHashtags] = useState(false);
    const [editedHashtags, setEditedHashtags] = useState((post.hashtags || []).join(' '));
    const [isEditingPrompt, setIsEditingPrompt] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState(post.imagePrompt || '');
    const queryClient = useQueryClient();

    // Image prompt update and regeneration mutation
    const updatePromptAndRegenerateMutation = useMutation(
        ({ id, prompt }) => postsAPI.updatePromptAndRegenerate(id, prompt),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Image regeneration started with new prompt!');
                setIsEditingPrompt(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to regenerate images');
            },
        }
    );

    const handleEditPrompt = () => {
        setEditedPrompt(post.imagePrompt || '');
        setIsEditingPrompt(true);
    };

    const handleCancelEditPrompt = () => {
        setEditedPrompt(post.imagePrompt || '');
        setIsEditingPrompt(false);
    };

    const handleSavePrompt = () => {
        if (editedPrompt.trim() === '') {
            toast.error('Image prompt cannot be empty');
            return;
        }

        if (window.confirm('This will regenerate both images with the new prompt. Continue?')) {
            updatePromptAndRegenerateMutation.mutate({
                id: post._id,
                prompt: editedPrompt.trim()
            });
        }
    };

    // Fetch available platforms
    const { data: availablePlatforms } = useQuery(
        'availablePlatforms',
        postsAPI.getAvailablePlatforms,
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    const stripHtml = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    // Hashtags update mutation
    const updateHashtagsMutation = useMutation(
        ({ id, hashtags }) => postsAPI.updateHashtags(id, hashtags),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Hashtags updated!');
                setIsEditingHashtags(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update hashtags');
            },
        }
    );

    const handleEditHashtags = () => {
        setEditedHashtags((post.hashtags || []).join(' '));
        setIsEditingHashtags(true);
    };

    const handleCancelEditHashtags = () => {
        setEditedHashtags((post.hashtags || []).join(' '));
        setIsEditingHashtags(false);
    };

    const handleSaveHashtags = () => {
        // Parse hashtags from the text input
        const hashtagArray = editedHashtags
            .split(/[\s,]+/) // Split by spaces or commas
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0)
            .map(tag => tag.startsWith('#') ? tag : `#${tag}`) // Ensure # prefix
            .filter(tag => tag.length > 1); // Remove solo # characters

        updateHashtagsMutation.mutate({
            id: post._id,
            hashtags: hashtagArray
        });
    };

    const updateCaptionMutation = useMutation(
        ({ id, caption }) => postsAPI.updateCaption(id, caption),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Caption updated!');
                setIsEditingCaption(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update caption');
            },
        }
    );

    const handleEditCaption = () => {
        setEditedCaption(post.caption || '');
        setIsEditingCaption(true);
    };

    const handleCancelEditCaption = () => {
        setEditedCaption(post.caption || '');
        setIsEditingCaption(false);
    };

    const handleSaveCaption = () => {
        if (editedCaption.trim() === '') {
            toast.error('Caption cannot be empty');
            return;
        }

        updateCaptionMutation.mutate({
            id: post._id,
            caption: editedCaption.trim()
        });
    };

    const handleImageClick = (imageUrl, variant) => {
        setFullScreenImage({ url: imageUrl, variant });
    };

    const closeFullScreen = () => {
        setFullScreenImage(null);
    };

    const approveMutation = useMutation(
        ({ id, variant }) => postsAPI.approve(id, variant),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Post approved!');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to approve post');
            },
        }
    );

    const publishMutation = useMutation(postsAPI.publish, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Publishing started!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to publish post');
        },
    });

    const deleteMutation = useMutation(postsAPI.delete, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Post deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete post');
        },
    });

    const retryMutation = useMutation(postsAPI.retry, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Post regeneration started!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to retry post');
        },
    });

    // NEW: Platform update mutation
    const updatePlatformsMutation = useMutation(
        ({ id, platforms }) => postsAPI.updatePlatforms(id, platforms),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Platforms updated!');
                setShowPlatformSelector(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update platforms');
            },
        }
    );

    const handleApprove = (variant) => {
        approveMutation.mutate({ id: post._id, variant });
    };

    const handlePublish = () => {
        if (window.confirm('Publish this post immediately?')) {
            publishMutation.mutate(post._id);
        }
    };

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            deleteMutation.mutate(post._id);
        }
    };

    const handleRetry = () => {
        retryMutation.mutate(post._id);
    };

    // NEW: Platform management handlers
    const handleOpenPlatformSelector = () => {
        setSelectedPlatforms([...post.platforms]);
        setShowPlatformSelector(true);
    };

    const handleClosePlatformSelector = () => {
        setSelectedPlatforms([...post.platforms]);
        setShowPlatformSelector(false);
    };

    const handlePlatformToggle = (platformId) => {
        setSelectedPlatforms(prev => {
            if (prev.includes(platformId)) {
                return prev.filter(p => p !== platformId);
            } else {
                return [...prev, platformId];
            }
        });
    };

    const handleSavePlatforms = () => {
        if (selectedPlatforms.length === 0) {
            toast.error('Please select at least one platform');
            return;
        }

        updatePlatformsMutation.mutate({
            id: post._id,
            platforms: selectedPlatforms
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            queued: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            posting: 'bg-blue-100 text-blue-800',
            posted: 'bg-purple-100 text-purple-800',
            failed: 'bg-red-100 text-red-800',
        };

        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    // Helper function to get display URLs (public URLs for img tags)
    const getDisplayImageUrls = () => {
        return {
            sora: post.imageMetadata?.sora?.publicUrl || post.imageUrl_sora,
            whisk: post.imageMetadata?.whisk?.publicUrl || post.imageUrl_whisk
        };
    };

    const displayUrls = getDisplayImageUrls();
    const platforms = availablePlatforms?.data?.data || [];
    const canEditPlatforms = ['queued', 'approved'].includes(post.status);

    const cloneMutation = useMutation(
        (id) => postsAPI.clone(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Post cloned successfully!');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to clone post');
            },
        }
    );

    // Add clone handler
    const handleClone = () => {
        if (window.confirm('Clone this post?')) {
            cloneMutation.mutate(post._id);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
            <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {post.title || 'Untitled Post'}
                        </h3>
                        <div className="text-sm text-gray-600 mb-1 line-clamp-3">
                            {isEditingCaption ? (
                                <div className="mb-4">
        <textarea
            value={editedCaption}
            onChange={(e) => setEditedCaption(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={4}
            placeholder="Enter post caption..."
            maxLength={2000}
        />
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="text-xs text-gray-500">
                                            {editedCaption.length}/2000 characters
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={handleCancelEditCaption}
                                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveCaption}
                                                disabled={updateCaptionMutation.isLoading || editedCaption.trim() === ''}
                                                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                {updateCaptionMutation.isLoading ? (
                                                    <>
                                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckIcon className="h-3 w-3 mr-1" />
                                                        Save
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-0">
                                    <div className="text-sm text-gray-600 line-clamp-6">
                                        {stripHtml(post.caption || '')}
                                    </div>
                                    {['queued', 'approved'].includes(post.status) && (
                                        <button
                                            onClick={handleEditCaption}
                                            className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            Edit caption
                                        </button>
                                    )}
                                </div>

                            )}
                        </div>

                        {isEditingHashtags ? (
                            <div className="mb-4">
        <textarea
            value={editedHashtags}
            onChange={(e) => setEditedHashtags(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            placeholder="Enter hashtags separated by spaces (e.g. #marketing #socialmedia #content)"
            maxLength={500}
        />
                                <div className="flex items-center justify-between mt-2">
                                    <div className="text-xs text-gray-500">
                                        {editedHashtags.length}/500 characters • Separate hashtags with spaces
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={handleCancelEditHashtags}
                                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveHashtags}
                                            disabled={updateHashtagsMutation.isLoading}
                                            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {updateHashtagsMutation.isLoading ? (
                                                <>
                                                    <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckIcon className="h-3 w-3 mr-1" />
                                                    Save
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mb-4">
                                {post.hashtags && post.hashtags.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {post.hashtags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                            >
                        {tag}
                    </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-xs text-gray-400">No hashtags</div>
                                )}
                                {['queued', 'approved'].includes(post.status) && (
                                    <button
                                        onClick={handleEditHashtags}
                                        className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                    >
                                        {post.hashtags && post.hashtags.length > 0 ? 'Edit hashtags' : 'Add hashtags'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>



                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(post.status)}`}>
                        {post.status}
                    </span>
                </div>


                {/* Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {updatePromptAndRegenerateMutation.isLoading ? (
                        // Loading state during regeneration
                        <>
                            <div className="relative bg-gray-100 rounded-lg h-48 flex items-center justify-center">
                                <div className="text-center">
                                    <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                    <div className="text-sm text-gray-500">Generating OpenAi image...</div>
                                </div>
                            </div>
                            <div className="relative bg-gray-100 rounded-lg h-48 flex items-center justify-center">
                                <div className="text-center">
                                    <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                    <div className="text-sm text-gray-500">Generating Google image...</div>
                                </div>
                            </div>
                        </>
                    ) : (
                        // Normal image display
                        <>
                            {displayUrls.sora && (
                                <div className={`relative ${post.status === 'approved' && post.imageUrl_final === post.imageUrl_sora ? 'ring-2 ring-green-500' : ''}`}>
                                    <img
                                        src={displayUrls.sora}
                                        alt="OpenAi generated"
                                        className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => handleImageClick(displayUrls.sora, 'OpenAi')}
                                        onError={(e) => {
                                            // console.error('Failed to load Sora image:', displayUrls.sora);
                                            e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                            // console.log('✅ Sora image loaded successfully:', displayUrls.sora);
                                        }}
                                    />
                                    <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-900">
                            OpenAi {post.status === 'approved' && post.imageUrl_final === post.imageUrl_sora && '✅'}
                        </span>
                                    </div>
                                    {post.status === 'queued' && (
                                        <div className="absolute bottom-2 left-2 right-2">
                                            <button
                                                onClick={() => handleApprove('sora')}
                                                disabled={approveMutation.isLoading}
                                                className="w-full bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                            >
                                                ✅ Approve
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {displayUrls.whisk && (
                                <div className={`relative ${post.status === 'approved' && post.imageUrl_final === post.imageUrl_whisk ? 'ring-2 ring-green-500' : ''}`}>
                                    <img
                                        src={displayUrls.whisk}
                                        alt="Google generated"
                                        className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => handleImageClick(displayUrls.whisk, 'Google')}
                                        onError={(e) => {
                                            // console.error('Failed to load Whisk image:', displayUrls.whisk);
                                            e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                            // console.log('✅ Whisk image loaded successfully:', displayUrls.whisk);
                                        }}
                                    />
                                    <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-900">
                            Google {post.status === 'approved' && post.imageUrl_final === post.imageUrl_whisk && '✅'}
                        </span>
                                    </div>
                                    {post.status === 'queued' && (
                                        <div className="absolute bottom-2 left-2 right-2">
                                            <button
                                                onClick={() => handleApprove('whisk')}
                                                disabled={approveMutation.isLoading}
                                                className="w-full bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                            >
                                                ✅ Approve
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Image Prompt Editing */}
                {isEditingPrompt && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="text-sm font-medium text-amber-800 mb-2">Edit Image Prompt</h4>
                        <textarea
                            value={editedPrompt}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            className="w-full p-3 border border-amber-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            rows={3}
                            placeholder="Describe the image you want to generate..."
                            maxLength={1000}
                        />
                        <div className="flex items-center justify-between mt-2">
                            <div className="text-xs text-amber-600">
                                {editedPrompt.length}/1000 characters • This will regenerate both OpenAi and Google images
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCancelEditPrompt}
                                    className="px-3 py-1 text-sm text-amber-700 hover:text-amber-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSavePrompt}
                                    disabled={updatePromptAndRegenerateMutation.isLoading || editedPrompt.trim() === ''}
                                    className="inline-flex items-center px-3 py-1 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 disabled:opacity-50"
                                >
                                    {updatePromptAndRegenerateMutation.isLoading ? (
                                        <>
                                            <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                            Regenerating...
                                        </>
                                    ) : (
                                        <>
                                            <RocketLaunchIcon className="h-3 w-3 mr-1" />
                                            Regenerate Images
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Prompt Button */}
                {!isEditingPrompt && ['queued', 'approved', 'failed'].includes(post.status) && (
                    <div className="mb-6 flex justify-center">
                        <button
                            onClick={handleEditPrompt}
                            className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors"
                        >
                            <Cog6ToothIcon className="h-4 w-4 mr-2" />
                            Edit Image Prompt & Regenerate
                        </button>
                    </div>
                )}

                {/* Platform Selector */}
                {showPlatformSelector && canEditPlatforms && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">Select Platforms</h4>
                            <button
                                onClick={handleClosePlatformSelector}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {platforms.map((platform) => (
                                <label
                                    key={platform.id}
                                    className={`flex items-center p-3 rounded-md border cursor-pointer transition-colors ${
                                        selectedPlatforms.includes(platform.id)
                                            ? 'border-blue-300 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedPlatforms.includes(platform.id)}
                                        onChange={() => handlePlatformToggle(platform.id)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-3 text-lg">{platform.icon}</span>
                                    <span className="ml-2 text-sm font-medium text-gray-900">
                                        {platform.name}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                                {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''} selected
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleClosePlatformSelector}
                                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSavePlatforms}
                                    disabled={updatePlatformsMutation.isLoading || selectedPlatforms.length === 0}
                                    className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {updatePlatformsMutation.isLoading ? (
                                        <>
                                            <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckIcon className="h-3 w-3 mr-1" />
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                        {post.status === 'approved' && (
                            <button
                                onClick={handlePublish}
                                disabled={publishMutation.isLoading}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                <RocketLaunchIcon className="h-4 w-4 mr-1" />
                                {publishMutation.isLoading ? 'Publishing...' : 'Publish Now'}
                            </button>
                        )}

                        {(post.status === 'queued' || post.status === 'failed') && (
                            <button
                                onClick={handleRetry}
                                disabled={retryMutation.isLoading}
                                className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                                    post.status === 'failed'
                                        ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100 focus:ring-red-500'
                                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500'
                                }`}
                            >
                                <ArrowPathIcon className="h-4 w-4 mr-1" />
                                {retryMutation.isLoading ? 'Retrying...' : post.status === 'failed' ? 'Reset Failed Post' : 'Reset'}
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleClone}
                        disabled={cloneMutation.isLoading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                        {cloneMutation.isLoading ? 'Cloning...' : 'Clone'}
                    </button>

                    <button
                        onClick={handleDelete}
                        disabled={deleteMutation.isLoading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                    </button>
                </div>

                {/* Platform info */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="text-sm text-gray-500">
                                <span className="font-medium">Platforms:</span>
                                <div className="flex items-center space-x-2 mt-1">
                                    {post.platforms && post.platforms.length > 0 ? (
                                        post.platforms.map((platformId) => {
                                            const platform = platforms.find(p => p.id === platformId);
                                            return (
                                                <span
                                                    key={platformId}
                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                                                >
                                                    <span className="mr-1">{platform?.icon || '🌐'}</span>
                                                    {platform?.name || platformId}
                                                </span>
                                            );
                                        })
                                    ) : (
                                        <span className="text-gray-400 text-xs">No platforms selected</span>
                                    )}
                                </div>
                            </div>

                            {canEditPlatforms && !showPlatformSelector && (
                                <button
                                    onClick={handleOpenPlatformSelector}
                                    className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit platforms"
                                >
                                    <Cog6ToothIcon className="h-3 w-3 mr-1" />
                                    Edit
                                </button>
                            )}
                        </div>

                        <div className="text-sm text-gray-500">
                            {new Date(post.createdAt).toLocaleDateString()}
                        </div>
                    </div>

                    {/* Publishing Results (for posted posts) */}
                    {post.status === 'posted' && post.publishingAttempts && post.publishingAttempts.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-500 mb-2">Publishing Results:</div>
                            <div className="grid grid-cols-1 gap-2">
                                {post.publishingAttempts.map((attempt, index) => {
                                    // Special handling for LinkedIn with multiple accounts
                                    if (attempt.platform === 'linkedin' && attempt.linkedinDetails) {
                                        return attempt.linkedinDetails.map((detail, detailIndex) => (
                                            <div
                                                key={`${index}-${detailIndex}`}
                                                className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                                                    detail.success
                                                        ? 'bg-green-50 text-green-700'
                                                        : 'bg-red-50 text-red-700'
                                                }`}
                                            >
                                                <div className="flex items-center">
                                <span className="font-medium mr-1">
                                    LinkedIn ({detail.accountType}):
                                </span>
                                                    <span>
                                    {detail.success ? 'Published' : 'Failed'}
                                </span>
                                                </div>
                                                {detail.success && detail.postUrl && (
<a
                                                    href={detail.postUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ml-2 text-blue-600 hover:text-blue-800 underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                    >
                                                    View Post →
                                                    </a>
                                                    )}
                                </div>
                                ));
                                }

                                    // Regular handling for other platforms
                                    return (
                                    <div
                                    key={index}
                                 className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                                     attempt.success
                                         ? 'bg-green-50 text-green-700'
                                         : 'bg-red-50 text-red-700'
                                 }`}
                            >
                                <div className="flex items-center">
                            <span className="font-medium mr-1">
                                {attempt.platform}:
                            </span>
                                    <span>
                                {attempt.success ? 'Published' : 'Failed'}
                            </span>
                                </div>
                                {attempt.success && (attempt.postUrl || attempt.tweetUrl) && (
<a
                                    href={attempt.postUrl || attempt.tweetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-blue-600 hover:text-blue-800 underline"
                                    onClick={(e) => e.stopPropagation()}
                                    >
                                    View Post →
                                    </a>
                                    )}
                            </div>
                            );
                            }).flat()}
                        </div>
                        </div>
                        )}
                </div>
            </div>
            {/* Full Screen Image Modal */}
            {fullScreenImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                    onClick={closeFullScreen}
                >
                    <div className="relative max-w-full max-h-full">
                        <img
                            src={fullScreenImage.url}
                            alt={`${fullScreenImage.variant} generated - Full screen`}
                            className="max-w-full max-h-full object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            onClick={closeFullScreen}
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                        <div className="absolute bottom-4 left-4 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
                            {fullScreenImage.variant} variant
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
}

export default PostCard;